import { Component } from 'react';
import type { ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo: errorInfo.componentStack || null,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    // Reload the page to reset the app state
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
          <Card className="max-w-2xl w-full border-red-200 bg-white shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    Something went wrong
                  </h1>
                  <p className="text-gray-700 mb-4">
                    We encountered an unexpected error. Please try refreshing the page.
                  </p>
                  
                  {this.state.error && (
                    <details className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2">
                        Error Details
                      </summary>
                      <div className="text-xs font-mono text-red-600 whitespace-pre-wrap break-all">
                        <p className="font-bold mb-2">{this.state.error.toString()}</p>
                        {this.state.errorInfo && (
                          <p className="text-gray-600 mt-2">{this.state.errorInfo}</p>
                        )}
                      </div>
                    </details>
                  )}

                  <div className="flex gap-3">
                    <Button
                      onClick={this.handleReset}
                      className="bg-[#166B93] hover:bg-[#166B93]/90"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh Page
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => window.history.back()}
                    >
                      Go Back
                    </Button>
                  </div>

                  <p className="text-sm text-gray-500 mt-6">
                    If the problem persists, please contact our support team.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

